<template>
    <div class="photo-carousel" :class="{ 'photo-carousel--landscape': variant === 'landscape' }">
        <!-- Основное фото -->
    <div class="photo-carousel-main">
            <button
                v-if="photos.length > 1"
                class="carousel-arrow carousel-arrow--left"
                :class="{ 'is-disabled': currentIndex === 0 }"
                @click="prev"
                :disabled="currentIndex === 0"
                type="button"
                aria-label="previous photo"
                :title="currentIndex === 0 ? 'Начало' : 'Предыдущее фото'">
                <span>&lsaquo;</span>
            </button>
            <div class="carousel-photo-wrapper" :class="{ 'is-clickable': canOpenLightbox }" @click="onPhotoClick">
                <!-- If currentPhoto exists and has a URL, show image -->
                <img
                    v-if="currentPhoto && currentPhoto.url"
                    :src="currentPhoto.url"
                    :alt="photoSeasonName(currentPhoto)"
                    class="carousel-photo-img"
                    decoding="async"
                    fetchpriority="high"
                />

                <!-- If currentPhoto exists but url is explicitly null -> show 'Фото нет' placeholder for that season -->
                <div v-else-if="currentPhoto && currentPhoto.url === null" class="carousel-photo-no-photo">
                    <span class="carousel-no-photo-text">Фото нет</span>
                </div>

                <!-- If there is no currentPhoto (photos list empty) -> show fallback or initials placeholder -->
                <div v-else class="carousel-photo-placeholder">
                    <slot name="placeholder">
                        <span class="carousel-placeholder-text">{{ initials }}</span>
                    </slot>
                </div>

                <div v-if="currentPhoto && photoLabel(currentPhoto)" class="carousel-season-label">{{ photoLabel(currentPhoto) }}</div>
            </div>
            <button
                v-if="photos.length > 1"
                class="carousel-arrow carousel-arrow--right"
                :class="{ 'is-disabled': currentIndex === photos.length - 1 }"
                @click="next"
                :disabled="currentIndex === photos.length - 1"
                type="button"
                aria-label="next photo"
                :title="currentIndex === photos.length - 1 ? 'Конец' : 'Следующее фото'">
                <span>&rsaquo;</span>
            </button>
        </div>

        <!-- Thumbs removed: only arrows remain for navigation -->
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    // Массив: [{seasonId, seasonName, seasonShort, url}]
    photos: {
        type: Array,
        default: () => []
    },
    // Фоллбэк URL (если нет фото по сезонам)
    fallbackUrl: {
        type: String,
        default: null
    },
    // Инициалы для плейсхолдера
    initials: {
        type: String,
        default: ''
    },
    variant: {
        type: String,
        default: 'portrait', // portrait | landscape
        validator: (value) => ['portrait', 'landscape'].includes(value),
    },
    /** When set, carousel jumps to the slide for this season_id. */
    activeSeasonId: {
        type: [Number, String],
        default: null,
    },
});

const emit = defineEmits(['open-lightbox', 'season-change']);

const currentIndex = ref(0);
const syncingFromSeason = ref(false);

const photoSeasonId = (photo) => {
    const raw = photo?.season_id ?? photo?.seasonId;
    return raw === null || raw === undefined || raw === '' ? null : Number(raw);
};

const photoSeasonName = (photo) => photo?.seasonName ?? photo?.season_name ?? '';

const photoLabel = (photo) => photo?.caption ?? photo?.seasonName ?? photo?.season_name ?? '';

const currentPhoto = computed(() => {
    // If we have an explicit photos array (may contain items with url === null to represent "no photo")
    if (props.photos.length > 0) {
        return props.photos[currentIndex.value];
    }
    // No season photos in carousel -> use fallback image if provided (this object has a url)
    if (props.fallbackUrl) {
        return { url: props.fallbackUrl, seasonName: '', seasonShort: '' };
    }
    return null;
});

const canOpenLightbox = computed(() => Boolean(currentPhoto.value?.url));

const onPhotoClick = () => {
    if (!canOpenLightbox.value) return;
    emit('open-lightbox', {
        url: currentPhoto.value.url,
        index: currentIndex.value,
        photo: currentPhoto.value,
    });
};

const emitSeasonChange = () => {
    if (syncingFromSeason.value) return;
    const seasonId = photoSeasonId(props.photos[currentIndex.value]);
    if (seasonId === null) return;
    if (Number(props.activeSeasonId) === seasonId) return;
    emit('season-change', seasonId);
};

const syncIndexToSeason = (seasonId) => {
    if (seasonId === null || seasonId === undefined || seasonId === '') return;
    if (props.photos.length === 0) return;

    const idx = props.photos.findIndex((photo) => photoSeasonId(photo) === Number(seasonId));
    if (idx < 0) return;
    if (idx === currentIndex.value) return;

    syncingFromSeason.value = true;
    currentIndex.value = idx;
    syncingFromSeason.value = false;
};

const prev = () => {
    if (props.photos.length === 0) return;
    if (currentIndex.value === 0) return; // don't wrap — stop at first
    currentIndex.value = currentIndex.value - 1;
    emitSeasonChange();
};

const next = () => {
    if (props.photos.length === 0) return;
    if (currentIndex.value >= props.photos.length - 1) return; // don't wrap — stop at last
    currentIndex.value = currentIndex.value + 1;
    emitSeasonChange();
};

const goTo = (index) => {
    currentIndex.value = index;
    emitSeasonChange();
};

watch(
    () => props.activeSeasonId,
    (seasonId) => {
        syncIndexToSeason(seasonId);
    },
    { immediate: true },
);

// Сбрасываем индекс при изменении списка фото, затем снова синхронизируем с сезоном
watch(() => props.photos, () => {
    if (currentIndex.value >= props.photos.length) {
        currentIndex.value = Math.max(0, props.photos.length - 1);
    }
    syncIndexToSeason(props.activeSeasonId);
});
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.photo-carousel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
}

.photo-carousel--landscape {
    align-items: stretch;

    .photo-carousel-main {
        width: 100%;
        justify-content: center;
    }

    .carousel-photo-wrapper {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
        max-width: 360px;
        height: auto;
        aspect-ratio: 16 / 10;
        border-radius: 12px;
        background: #fff;
        border: 1px solid #e5e7eb;
    }

    .carousel-photo-img {
        object-fit: contain;
        object-position: center;
    }

    .carousel-photo-placeholder {
        background: #fff;
    }

    .carousel-photo-no-photo {
        background: #fff;
    }

    .carousel-no-photo-text {
        background: transparent;
        box-shadow: none;
    }

    .carousel-placeholder-text {
        font-size: 1.5rem;
    }
}

.photo-carousel-main {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    position: relative;
}

.carousel-photo-wrapper {
    width: 150px;
    height: 200px;
    border-radius: 10px;
    overflow: hidden;
    background: #f3f4f6;
    position: relative;
    flex-shrink: 0;

    &.is-clickable {
        cursor: zoom-in;
    }
}

.carousel-photo-img {
    width: 100%;
    height: 100%;
    @include person-photo-cover;
}

.carousel-photo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e5e7eb;
}

.carousel-placeholder-text {
    font-size: 2.5rem;
    font-weight: 700;
    color: #9ca3af;
}

.carousel-photo-no-photo {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
    color: #6b7280;
    text-align: center;
    padding: 0.5rem;
}

.carousel-no-photo-text {
    font-size: 0.9rem;
    font-weight: 700;
    color: #475569;
    background: rgba(255,255,255,0.6);
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(16,24,40,0.04);
}

.carousel-season-label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    text-align: center;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.2rem 0.25rem;
    letter-spacing: 0.02em;
}

.carousel-arrow {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    transition: color 0.15s;
    flex-shrink: 0;
    transition: color 0.15s, opacity 0.15s;

    span {
        font-size: 1.75rem;
        line-height: 1;
        font-weight: 300;
    }

    &:hover {
        color: #1e293b;
    }
    &.is-disabled,
    &[disabled] {
        color: #cbd5e1;
        cursor: default;
        opacity: 0.45;
        pointer-events: none;
    }
}
</style>
